<template>
  <div class="group relative flex aspect-[320/370] flex-col justify-end overflow-hidden rounded-tr-[2.5rem]" :style="`--scroll-height: ${scrollHeight}px;`">
    <!-- BG IMAGE -->
    <BetterPortraitImage
      v-if="data.use_image_from_betterPortrait"
      :email="data.email"

      class="
        absolute inset-0 size-full scale-100 object-cover transition-transform duration-300 ease-in-out
        will-change-transform
        md:group-hover-focus:scale-105
      "

      :alt="data.title"
    />

    <AppImage
      v-else-if="data.image"
      class="
        absolute inset-0 size-full scale-100 object-cover transition-transform duration-300 ease-in-out
        will-change-transform
        md:group-hover-focus:scale-105
      "

      :src="data.image"
      :alt="useMapAltText(data.image)"
    />

    <!-- GRADIENT -->
    <div class="absolute inset-0 gradient" />

    <!-- TEXT -->
    <div class="relative mx-6 text-white">
      <!-- Title -->
      <div class="mb-3 flex items-start justify-between typo-heading-md">
        <h3 class="line-clamp-4">
          {{ data.title }}
        </h3>
        <div class="relative size-[1.5em] shrink-0 max-md:hidden">
          <IconPlus
            class="
              absolute inset-0 opacity-0 transition-opacity duration-500
              md:opacity-100 md:group-hover-focus:opacity-0
            "
          />
          <IconMinus
            class="
              absolute inset-0 opacity-100 transition-opacity duration-500
              md:opacity-0 md:group-hover-focus:opacity-100
            "
          />
        </div>
      </div>

      <!-- Collapse -->

      <div
        ref="collapse"
        class="
          h-auto transition-[height,opacity] duration-[400ms]
          md:h-0 md:opacity-0 md:group-hover-focus:h-[var(--scroll-height)] md:group-hover-focus:opacity-100
        "
      >
        <div class="pb-12">
          <!-- Function -->
          <p class="mb-1 line-clamp-3 typo-body-xl">
            {{ data.function }}
          </p>

          <!-- Phone -->
          <a
            v-if="data.phone"
            class="mb-1 block w-fit transition-colors typo-body-xl hover:text-theme-200"
            :href="`tel:${data.phone}`"
          >
            {{ `${$t('phone_short')} ${data.phone}` }}
          </a>

          <!-- Email -->
          <a
            v-if="data.email"
            class="block w-fit transition-colors typo-body-xl hover:text-theme-200"
            :href="`mailto:${data.email}`"
          >
            {{ data.email }}
          </a>
        </div>
      </div>

      <!-- Border Bottom -->
      <div
        class="
          absolute inset-x-0 bottom-6 h-0 scale-x-100 border bg-white transition-transform delay-100
          duration-300 ease-in-out
          md:scale-x-0 md:group-hover-focus:scale-x-100
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TeamMemberFragment } from '#gql'

defineProps<{
  data: Enforce<TeamMemberFragment>
}>()

const collapse = ref<HTMLElement | null>(null)
const scrollHeight = ref(0)

async function handleResize() {
  scrollHeight.value = collapse.value?.scrollHeight ?? 0
}

const debouncedHandleResize = useDebounceFn(() => {
  if (window.innerWidth > 768) {
    handleResize()
  }
}, 500)

onMounted(() => {
  window.addEventListener('resize', debouncedHandleResize)
  handleResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', debouncedHandleResize)
})
</script>

<style scoped>
.gradient {
background: linear-gradient(180deg, transparent 40%, rgba(11, 21, 44, 0.1) 60%, rgba(11, 21, 44, 0.5) 100%);
}
</style>
